// src/components/accueil/RechercheGlobale.tsx — SCE Issues
// Recherche de l'accueil V2 (Lot 9) : un N° de problème, un titre, un N° de
// série, un OF, un PN — et on ouvre la fiche.
//
// Élargie le 05/10 (`utils/rechercheGlobale`) : projets, chefs de projet,
// références qualité (FNC-T, NC-S, N° de plan, EDE), NVS, code de désignation,
// modèle, et caractéristiques (« atex 1 », « 100 bar ») — qui ouvrent la page
// Équipements filtrée. Une ligne dit pourquoi un résultat sort quand ce n'est
// pas lisible sur sa ligne.
//
// Aucune requête par frappe : les problèmes sont ceux de la page (accueil) ou,
// ailleurs, lus au premier focus ; le référentiel est le cache mémoire
// d'equipements.api (chargé au premier focus). Un fournisseur ne cherche que
// dans ses problèmes : le référentiel est hors de son périmètre.
//
// Bureau : au centre de la barre de titre des quatre pages de la barre du bas
// (`RechercheEntete`), « / » y place le curseur. Champ vide : « Rechercher
// un … » fait défiler ce qu'on peut y taper (problème, N° de série, OF, PN) —
// plus court qu'une énumération qui se faisait couper.
// Téléphone : sous la barre de l'accueil, pleine largeur.

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, ChevronRight, CircleDashed, Clock, Search } from 'lucide-react'
import { listAllEquipements } from '@/api/equipements.api'
import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import { ENTETE_LARGE } from '@/components/layout/Header'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useIssues } from '@/hooks/useIssues'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { lienChefProjet, lienEquipement, lienFnct, lienProbleme, lienProjet } from '@/utils/liensFiches'
import { useFnct } from '@/hooks/useFnct'
import { accesFnct } from '@/utils/droitsFnct'
import { FNCT_MASQUEE } from '@/config/fnct.config'
import type { Fnct } from '@/types/fnct.types'
import { TITRES_GENRES, indexerReferentiel, rechercher, type Resultat } from '@/utils/rechercheGlobale'
import type { Equipement, Issue } from '@/types/issue.types'
import { EtatProbleme } from '@/components/ui/EtatProbleme'

/** Ce qu'on peut chercher, dans l'ordre du défilement. */
const MOTS_DEFILES = ['problème', 'N° de série', 'OF', 'PN', 'chef de projet', 'client', 'NVS'] as const

/**
 * Mots tout juste cherchables : pastille « Nouveau » à côté, jusqu'à la date
 * dite (exclue), puis plus rien — pas besoin de revenir l'enlever.
 */
const MOTS_NOUVEAUX: Partial<Record<(typeof MOTS_DEFILES)[number], string>> = {
  client: '2026-11-03', // Customer Name de Windchill, 20/10
}

function estNouveau(mot: (typeof MOTS_DEFILES)[number]): boolean {
  const fin = MOTS_NOUVEAUX[mot]
  return fin !== undefined && Date.now() < Date.parse(`${fin}T00:00:00`)
}
const DUREE_MOT_MS = 2400

/** Clé React d'un résultat, unique dans la liste. */
function cleResultat(r: Resultat): string {
  switch (r.genre) {
    case 'projet':
      return `pr-${r.pn}`
    case 'chef':
      return `c-${r.nom}`
    case 'filtre':
      return `f-${r.critere}-${r.valeur}`
    case 'probleme':
      return `p-${r.issue.id}`
    case 'equipement':
      return `e-${r.equipement.numeroSerie}`
    case 'fnct':
      return `n-${r.numero}`
    default: {
      const jamais: never = r
      return jamais
    }
  }
}

function pluriel(n: number, mot: string): string {
  return `${n} ${mot}${n > 1 ? 's' : ''}`
}

/**
 * La recherche au centre de la barre de titre, au bureau seulement.
 * Sans `issues`, les problèmes sont lus au premier focus.
 */
export function RechercheEntete({ issues }: { issues?: readonly Issue[] }): JSX.Element | null {
  const large = useMediaQuery(ENTETE_LARGE)
  return large ? <RechercheGlobale issues={issues} variante="entete" /> : null
}

/** Les FNC-T, lues au premier focus et seulement pour qui y a accès (lot 5 du § 14). */
function ChargeurFnct({ onCharge }: { onCharge: (fncts: readonly Fnct[]) => void }): null {
  const { fncts, loading } = useFnct()
  useEffect(() => { if (!loading) onCharge(fncts) }, [fncts, loading, onCharge])
  return null
}

/** Monté au premier focus d'une page qui n'a pas ses problèmes sous la main. */
function ChargeurProblemes({ onCharge }: { onCharge: (issues: readonly Issue[]) => void }): null {
  const { issues, loading } = useIssues()
  useEffect(() => { if (!loading) onCharge(issues) }, [issues, loading, onCharge])
  return null
}

export function RechercheGlobale({
  issues: issuesPage,
  variante,
}: {
  issues?: readonly Issue[]
  variante: 'entete' | 'page'
}): JSX.Element {
  const [issuesLus, setIssuesLus] = useState<readonly Issue[]>([])
  const issues = issuesPage ?? issuesLus
  const { isFournisseur, user } = useCurrentUser()
  const avecFnct = !isFournisseur && !FNCT_MASQUEE && accesFnct(user?.upn)
  const [fncts, setFncts] = useState<readonly Fnct[]>([])
  const ouvrirFiche = useOuvrirFiche()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [ouvert, setOuvert] = useState(false)
  const [actif, setActif] = useState(0)
  const [equipements, setEquipements] = useState<Equipement[]>([])
  const conteneur = useRef<HTMLDivElement>(null)
  const champ = useRef<HTMLInputElement>(null)
  const idListe = useId()

  // Référentiel au premier focus seulement : qui ne cherche pas ne le paie pas.
  const [referentielDemande, setReferentielDemande] = useState(false)
  useEffect(() => {
    if (!referentielDemande || isFournisseur) return
    let annule = false
    listAllEquipements()
      .then((eqs) => { if (!annule) setEquipements(eqs) })
      .catch(() => { /* recherche limitée aux problèmes */ })
    return () => { annule = true }
  }, [referentielDemande, isFournisseur])

  // Le référentiel est lu une fois (modèle, code décodé, NVS) : chaque frappe
  // ne fait plus que comparer des textes.
  const referentiel = useMemo(() => indexerReferentiel(equipements), [equipements])
  const resultats = useMemo<Resultat[]>(() => rechercher(q, issues, referentiel, avecFnct ? fncts : []), [q, issues, referentiel, avecFnct, fncts])

  useEffect(() => setActif(0), [q])

  // Mot défilant de l'en-tête, seulement tant que le champ est vide.
  const [mot, setMot] = useState(0)
  const defile = variante === 'entete' && q === ''
  useEffect(() => {
    if (!defile) return
    const minuterie = window.setInterval(() => setMot((m) => (m + 1) % MOTS_DEFILES.length), DUREE_MOT_MS)
    return () => window.clearInterval(minuterie)
  }, [defile])

  // « / » place le curseur dans la recherche (bureau), hors d'un champ de saisie.
  useEffect(() => {
    if (variante !== 'entete') return
    function auClavier(e: KeyboardEvent): void {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return
      const cible = e.target as HTMLElement | null
      if (cible && (cible.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(cible.tagName))) return
      e.preventDefault()
      champ.current?.focus()
    }
    window.addEventListener('keydown', auClavier)
    return () => window.removeEventListener('keydown', auClavier)
  }, [variante])

  // Fermeture au clic extérieur.
  useEffect(() => {
    if (!ouvert) return
    function auClic(e: MouseEvent): void {
      if (!conteneur.current?.contains(e.target as Node)) setOuvert(false)
    }
    document.addEventListener('mousedown', auClic)
    return () => document.removeEventListener('mousedown', auClic)
  }, [ouvert])

  function choisir(r: Resultat): void {
    setOuvert(false)
    setQ('')
    champ.current?.blur()
    switch (r.genre) {
      case 'projet':
        ouvrirFiche(lienProjet(r.pn))
        break
      case 'chef':
        ouvrirFiche(lienChefProjet(r.nom))
        break
      case 'filtre':
        // La page Équipements lit ce filtre dans l'état de navigation.
        navigate('/equipements', { state: { filtres: { [r.critere]: [r.valeur] } } })
        break
      case 'probleme':
        ouvrirFiche(lienProbleme(r.issue.id))
        break
      case 'equipement':
        ouvrirFiche(lienEquipement(r.equipement.numeroSerie))
        break
      case 'fnct':
        ouvrirFiche(lienFnct(r.numero))
        break
      default: {
        const jamais: never = r
        void jamais
      }
    }
  }

  function auClavier(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Escape') {
      setOuvert(false)
      champ.current?.blur()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOuvert(true)
      setActif((a) => Math.min(a + 1, Math.max(resultats.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActif((a) => Math.max(a - 1, 0))
    } else if (e.key === 'Enter') {
      const r = resultats[actif]
      if (r) {
        e.preventDefault()
        choisir(r)
      }
    }
  }

  const entete = variante === 'entete'
  const afficher = ouvert && q.trim().length >= 2

  return (
    <div ref={conteneur} style={{ position: 'relative', width: entete ? 'clamp(240px, 24vw, 360px)' : '100%' }}>
      {!issuesPage && referentielDemande && <ChargeurProblemes onCharge={setIssuesLus} />}
      {avecFnct && referentielDemande && <ChargeurFnct onCharge={setFncts} />}
      <label
        className="flex items-center"
        style={{
          position: 'relative',
          gap: 10,
          height: entete ? 40 : 44,
          padding: '0 12px',
          boxSizing: 'border-box',
          backgroundColor: 'var(--color-white)',
          border: entete ? '1px solid var(--color-black)' : '1px solid var(--color-border)',
          borderRadius: entete ? 8 : 10,
        }}
      >
        <Search size={18} aria-hidden="true" color={entete ? 'var(--color-black)' : 'var(--color-text-secondary)'} />
        <input
          ref={champ}
          type="search"
          role="combobox"
          aria-expanded={afficher}
          aria-controls={idListe}
          aria-autocomplete="list"
          aria-label="Rechercher un problème, un équipement, un projet"
          placeholder={entete ? undefined : 'Problème, N° de série, PN, NVS…'}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOuvert(true) }}
          onFocus={() => { setReferentielDemande(true); setOuvert(true) }}
          onKeyDown={auClavier}
          style={{
            flexGrow: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontFamily: 'inherit',
            // 16 px au téléphone : en dessous, iOS zoome sur le champ.
            fontSize: entete ? 15 : 16,
            color: 'var(--color-text-primary)',
          }}
        />
        {defile && (
          // Faux texte indicatif, par-dessus le champ (un placeholder ne
          // s'anime pas). Le nom accessible reste l'aria-label du champ.
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 40,
              right: 44,
              top: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            {/* Une seule ligne de texte : le mot suit la ligne de base, même
                taille, même gris que « Rechercher un ». */}
            <span style={{ whiteSpace: 'nowrap', fontSize: 15, lineHeight: '20px', color: 'var(--color-text-secondary)' }}>
              Rechercher un{' '}
              <span key={mot} className="recherche-mot">
                {MOTS_DEFILES[mot]}
                {MOTS_DEFILES[mot] && estNouveau(MOTS_DEFILES[mot]) && (
                  // Même pastille que « Nouveau » des éléments suivis (CarteElementsSuivis).
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 11,
                      fontWeight: 500,
                      lineHeight: '16px',
                      padding: '1px 6px',
                      borderRadius: 4,
                      verticalAlign: 'text-bottom',
                      color: 'var(--color-white)',
                      backgroundColor: 'var(--color-black)',
                    }}
                  >
                    Nouveau
                  </span>
                )}
              </span>
            </span>
          </span>
        )}
        {entete && (
          <kbd
            aria-hidden="true"
            style={{ fontFamily: 'inherit', fontSize: 12, color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '0 6px' }}
          >
            /
          </kbd>
        )}
      </label>

      {afficher && (
        <div
          id={idListe}
          role="listbox"
          aria-label="Résultats"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            // En-tête : centrée sous le champ (au centre de la barre), un peu
            // plus large que lui pour lire les titres.
            ...(entete
              ? { left: '50%', transform: 'translateX(-50%)', width: 'max(100%, 460px)' }
              : { left: 0, right: 0 }),
            zIndex: 60,
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.14)',
            maxHeight: '70vh',
            overflowY: 'auto',
          }}
        >
          {resultats.length === 0 && (
            <div style={{ padding: '14px 16px', fontSize: 14, color: 'var(--color-text-secondary)' }}>
              Aucun résultat.
            </div>
          )}
          {resultats.map((r, index) => (
            <div key={cleResultat(r)}>
              {resultats[index - 1]?.genre !== r.genre && <Groupe>{TITRES_GENRES[r.genre]}</Groupe>}
              <button
                type="button"
                role="option"
                aria-selected={index === actif}
                onMouseEnter={() => setActif(index)}
                onClick={() => choisir(r)}
                className="flex items-center justify-between w-full text-left"
                style={{
                  gap: 12,
                  minHeight: 48,
                  padding: '6px 16px',
                  border: 'none',
                  backgroundColor: index === actif ? 'var(--color-gray-5)' : 'transparent',
                  fontFamily: 'inherit',
                  color: 'var(--color-text-primary)',
                  cursor: 'pointer',
                }}
              >
                <LigneResultat r={r} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const STYLE_TITRE = { fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } as const
const STYLE_SOUS = { fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } as const

/** Le contenu d'une ligne : titre, contexte, et pourquoi elle sort quand ça ne se voit pas. */
function LigneResultat({ r }: { r: Resultat }): JSX.Element {
  const texte = (titre: string, sous: string, raison?: string, chiffres = false): JSX.Element => (
    <span className="flex flex-col min-w-0">
      <span style={{ ...STYLE_TITRE, fontVariantNumeric: chiffres ? 'tabular-nums' : undefined }}>{titre}</span>
      <span style={{ ...STYLE_SOUS, fontVariantNumeric: chiffres ? 'tabular-nums' : undefined }}>{sous}</span>
      {raison && <span style={{ ...STYLE_SOUS, color: 'var(--color-text-primary)' }}>{raison}</span>}
    </span>
  )
  const fleche = <ChevronRight size={16} aria-hidden="true" style={{ flexShrink: 0 }} />
  switch (r.genre) {
    case 'projet':
      return (
        <>
          {texte(
            r.pn,
            [r.cve, r.client, r.chef, pluriel(r.nbEquipements, 'équipement'), r.nbActifs > 0 ? `${r.nbActifs} problème${r.nbActifs > 1 ? 's' : ''} actif${r.nbActifs > 1 ? 's' : ''}` : '']
              .filter(Boolean)
              .join(' · '),
            undefined,
            true,
          )}
          {fleche}
        </>
      )
    case 'chef':
      return (
        <>
          {texte(r.nom, `${pluriel(r.nbProjets, 'projet')} · ${pluriel(r.nbEquipements, 'équipement')}`)}
          {fleche}
        </>
      )
    case 'filtre':
      return (
        <>
          {texte(`${r.libelle} : ${r.valeur}`, `${pluriel(r.nb, 'équipement')} · ouvre la page Équipements filtrée`)}
          {fleche}
        </>
      )
    case 'probleme':
      return (
        <>
          {texte(r.issue.titre, [r.issue.id, r.issue.numeroSerie, r.issue.pn].filter(Boolean).join(' · '), r.raison)}
          <EtatProbleme statut={r.issue.statut} criticite={r.issue.criticite} />
        </>
      )
    case 'fnct':
      return (
        <>
          {texte(r.titre, [r.numero, r.machines].filter(Boolean).join(' · '), r.raison)}
          <span className="inline-flex items-center" title={r.etat === 'close' ? 'Close' : r.etat === 'releve' ? 'En relevé' : 'Ouverte'} style={{ flexShrink: 0 }}>
            {r.etat === 'close' ? (
              <CheckCircle2 size={16} color="var(--color-traffic-green)" aria-label="Close" />
            ) : r.etat === 'releve' ? (
              <CircleDashed size={16} aria-label="En relevé" />
            ) : (
              <Clock size={16} aria-label="Ouverte" />
            )}
          </span>
        </>
      )
    case 'equipement':
      return texte(
        r.equipement.numeroSerie,
        [r.equipement.familleEquipement, r.equipement.of, r.equipement.pn].filter(Boolean).join(' · '),
        r.raison,
        true,
      )
    default: {
      const jamais: never = r
      return jamais
    }
  }
}

function Groupe({ children }: { children: string }): JSX.Element {
  return (
    <div style={{ padding: '10px 16px 4px', fontSize: 12, fontWeight: 500, color: 'var(--color-text-secondary)' }}>
      {children}
    </div>
  )
}
